import { useEffect, useId, useRef, useState } from 'react';

/**
 * Registro central de drawers abiertos.
 *
 * Cada drawer abierto ocupa una capa: recibe su propio z-index, solo el de
 * arriba responde a Escape y el scroll del fondo se libera cuando se cierra
 * el último. Sin esto, dos drawers apilados (por ejemplo el panel del curso
 * y el formulario de una tarea) comparten z-index y compiten por el foco.
 */

const BASE_Z_INDEX = 2000;
const LAYER_STEP = 20;
/** Los modales de Headless UI viven en 9999: los drawers se quedan debajo. */
const MAX_Z_INDEX = 8000;

const layers = [];
const listeners = new Set();

let keydownBound = false;
let scrollLockCount = 0;
let restoreBodyStyle = null;

const notify = () => {
    listeners.forEach((listener) => listener());
};

const handleKeyDown = (event) => {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;

    const top = layers[layers.length - 1];
    if (!top || top.lockCloseRef.current) return;

    event.stopPropagation();
    top.closeRef.current?.();
};

const bindKeyDown = () => {
    if (keydownBound || typeof document === 'undefined') return;
    document.addEventListener('keydown', handleKeyDown);
    keydownBound = true;
};

const unbindKeyDown = () => {
    if (!keydownBound || layers.length > 0 || typeof document === 'undefined') return;
    document.removeEventListener('keydown', handleKeyDown);
    keydownBound = false;
};

const lockBodyScroll = () => {
    scrollLockCount += 1;
    if (scrollLockCount > 1 || typeof document === 'undefined') return;

    const { body } = document;
    restoreBodyStyle = {
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight,
    };

    // Compensar la barra de scroll evita que el fondo salte al abrir.
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
};

const releaseBodyScroll = () => {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount > 0 || typeof document === 'undefined' || !restoreBodyStyle) return;

    document.body.style.overflow = restoreBodyStyle.overflow;
    document.body.style.paddingRight = restoreBodyStyle.paddingRight;
    restoreBodyStyle = null;
};

/**
 * @param {object} options
 * @param {boolean} options.isOpen
 * @param {() => void} [options.onRequestClose] Cierre por Escape (solo la capa superior).
 * @param {boolean} [options.lockClose] Ignora Escape, p. ej. mientras guarda.
 * @param {boolean} [options.lockBackgroundScroll]
 * @returns {{ zIndex: number, depth: number, isTopLayer: boolean }}
 */
export function useDrawerLayer({
    isOpen,
    onRequestClose,
    lockClose = false,
    lockBackgroundScroll = true,
} = {}) {
    const id = useId();
    const closeRef = useRef(onRequestClose);
    const lockCloseRef = useRef(lockClose);
    const [position, setPosition] = useState({ index: -1, total: 0 });

    useEffect(() => {
        closeRef.current = onRequestClose;
    }, [onRequestClose]);

    useEffect(() => {
        lockCloseRef.current = lockClose;
    }, [lockClose]);

    useEffect(() => {
        const readPosition = () => {
            setPosition({
                index: layers.findIndex((layer) => layer.id === id),
                total: layers.length,
            });
        };

        listeners.add(readPosition);
        readPosition();

        return () => {
            listeners.delete(readPosition);
        };
    }, [id]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const layer = { id, closeRef, lockCloseRef };
        layers.push(layer);
        bindKeyDown();
        if (lockBackgroundScroll) lockBodyScroll();
        notify();

        return () => {
            const index = layers.indexOf(layer);
            if (index !== -1) layers.splice(index, 1);
            if (lockBackgroundScroll) releaseBodyScroll();
            unbindKeyDown();
            notify();
        };
    }, [isOpen, id, lockBackgroundScroll]);

    const depth = position.index < 0 ? 0 : position.index;

    return {
        zIndex: Math.min(BASE_Z_INDEX + depth * LAYER_STEP, MAX_Z_INDEX),
        depth,
        isTopLayer: position.index >= 0 && position.index === position.total - 1,
    };
}
